import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Lottie from "lottie-react";
import { BASE_URL } from "../config";
import { BUSINESS_TYPES } from "../data/businessTypeConfig";
import styles from "../styles/businessType.module.css";

// ── Lottie assets per business type ─────────────────────────────
const LOTTIE_ASSETS = {
  fitness: [
    require("../assets/lottie/fitness/acroyoga.json"),
    require("../assets/lottie/fitness/strength.json"),
    require("../assets/lottie/fitness/kicking.json"),
    require("../assets/lottie/fitness/squats.json"),
  ],
  beauty: [
    require("../assets/lottie/beauty/scissors.json"),
    require("../assets/lottie/beauty/barber-pole.json"),
    require("../assets/lottie/beauty/barber-chair.json"),
    require("../assets/lottie/beauty/manicure.json"),
    require("../assets/lottie/beauty/makeup.json"),
  ],
  wellness: [
    require("../assets/lottie/wellness/massage.json"),
    require("../assets/lottie/wellness/brain.json"),
    require("../assets/lottie/wellness/spa.json"),
    require("../assets/lottie/wellness/herbs.json"),
  ],
  business: [
    require("../assets/lottie/business/consultation.json"),
    require("../assets/lottie/business/b2b.json"),
    require("../assets/lottie/business/accounting.json"),
    require("../assets/lottie/business/meeting.json"),
    require("../assets/lottie/business/drone.json"),
  ],
  medical: [
    require("../assets/lottie/medical/doctor-male.json"),
    require("../assets/lottie/medical/doctor-female.json"),
    require("../assets/lottie/medical/report.json"),
    require("../assets/lottie/medical/pharmacy.json"),
    require("../assets/lottie/medical/teeth.json"),
    require("../assets/lottie/medical/appointment.json"),
  ],
  other: [
    require("../assets/lottie/other/confetti.json"),
    require("../assets/lottie/other/champagne.json"),
    require("../assets/lottie/other/dog.json"),
    require("../assets/lottie/other/cleaning.json"),
  ],
};

// ── Lottie Carousel Component ───────────────────────────────────
const LottieCarousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!items || items.length <= 1) return;
    const timer = setInterval(() => {
      const next = (currentIndex + 1) % items.length;
      setNextIndex(next);
      setAnimating(true);
      setTimeout(() => {
        setCurrentIndex(next);
        setNextIndex(null);
        setAnimating(false);
      }, 400);
    }, 2500);
    return () => clearInterval(timer);
  }, [items, currentIndex]);

  if (!items || items.length === 0) return null;

  return (
    <div className={styles.carouselWrap}>
      <div
        key={`current-${currentIndex}`}
        className={`${styles.carouselItem} ${animating ? styles.slideOutRight : ""}`}
      >
        <Lottie animationData={items[currentIndex]} loop autoplay style={{ width: "100%", height: "100%" }} />
      </div>
      {nextIndex !== null && (
        <div
          key={`next-${nextIndex}`}
          className={`${styles.carouselItem} ${styles.slideInFromLeft}`}
        >
          <Lottie animationData={items[nextIndex]} loop autoplay style={{ width: "100%", height: "100%" }} />
        </div>
      )}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────
const BusinessType = ({ onNext, onBack }) => {
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token && onBack) {
      onBack();
    }
  }, []);

  const handleContinue = async () => {
    if (!selected) return;
    setSaving(true);

    try {
      const businessId = localStorage.getItem("business_id");
      const authToken = localStorage.getItem("auth_token");

      await axios.put(
        `${BASE_URL}/business/update_profile_info`,
        { id: businessId, businessType: selected },
        { headers: { Authorization: authToken } }
      );

      localStorage.setItem("businessType", selected);
    } catch (e) {
      console.log("BusinessType save error:", e);
    }

    setSaving(false);
    onNext({ businessType: selected });
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h2 className={styles.title}>מה תחום העסק שלך?</h2>
        <p className={styles.subtitle}>
          נתאים לך הגדרות ושירותים לפי הבחירה
        </p>

        <div className={styles.grid}>
          {BUSINESS_TYPES.map((t) => {
            const isActive = selected === t.id;
            const lotties = LOTTIE_ASSETS[t.id];
            const hasLottie = lotties && lotties.length > 0;

            return (
              <div
                key={t.id}
                className={`${styles.card} ${isActive ? styles.cardActive : ""}`}
                style={
                  isActive
                    ? { backgroundColor: t.light, borderColor: t.color }
                    : {}
                }
                onClick={() => setSelected(t.id)}
              >
                {hasLottie ? (
                  <div
                    className={styles.lottieBox}
                    style={{
                      backgroundColor: isActive ? t.color : "#F3F4F6",
                    }}
                  >
                    <LottieCarousel
                      items={lotties}
                      isActive={isActive}
                      color={t.color}
                    />
                  </div>
                ) : (
                  <div
                    className={styles.emojiWrap}
                    style={{
                      backgroundColor: isActive ? t.color : "#F3F4F6",
                    }}
                  >
                    <span className={styles.emoji}>{t.emoji}</span>
                  </div>
                )}
                <span
                  className={styles.cardName}
                  style={isActive ? { color: t.color } : {}}
                >
                  {t.name}
                </span>
                {isActive && (
                  <div
                    className={styles.badge}
                    style={{ backgroundColor: t.color }}
                  >
                    <span className={styles.badgeTick}>&#10003;</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <button
            className={styles.continueBtn}
            onClick={handleContinue}
            disabled={!selected || saving}
          >
            {saving ? "שומר..." : "המשך"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessType;
